import {debounce} from '@github/mini-throttle'
import {useGetChatState} from '@github-ui/copilot-chat-contexts/ObservableChatContext'
import {isCopilotPath, isGitHubHomePath} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {CopilotChatAction, CopilotChatState} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import type {CopilotChatOrg} from '@github-ui/copilot-chat-types'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import type {Dispatch, PropsWithChildren, ReactNode} from 'react'
import {createContext, use, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'

import {useTraceDispatchMaybe} from '../components/tracing/TraceProvider'
import {usePlugins} from '../plugin/ImmersivePluginsProvider'
import {CopilotChatManager} from './copilot-chat-manager'
import type {ContributedInstructionSnapshot} from './instruction-rules'

export const CopilotChatManagerContext = createContext<CopilotChatManager | null>(null)
const EMPTY_CONTRIBUTED_INSTRUCTIONS: ContributedInstructionSnapshot = []
const subscribeToNothing = () => () => {}
const getEmptyContributedInstructions = () => EMPTY_CONTRIBUTED_INSTRUCTIONS
const managerKeys = new WeakMap<CopilotChatManager, number>()
let managerKeySequence = 0

function getManagerKey(manager: CopilotChatManager): number {
  const existingKey = managerKeys.get(manager)
  if (existingKey !== undefined) return existingKey
  managerKeySequence += 1
  managerKeys.set(manager, managerKeySequence)
  return managerKeySequence
}

export async function fetchImplicitContextForCurrentPage(
  manager: Pick<CopilotChatManager, 'fetchImplicitContext'>,
): Promise<void> {
  const hash = window.location.hash
  const pathname = window.location.pathname
  const url = hash ? `${pathname}${hash}` : pathname

  // Skip copilot internal URLs — these are chat navigation routes (e.g. /copilot/c/{threadId}),
  // not actual page context. Implicit context should only be fetched for host pages.
  if (isCopilotPath(pathname)) {
    return
  }

  // Skip the GitHub home/dashboard pages (github.com, github.com/dashboard). Users open chat
  // here with general intent, so attaching the home URL as a github-url reference is arbitrary
  // and adds no value.
  if (isGitHubHomePath(pathname)) {
    return
  }

  const urlParts = url.slice(1).split('/')
  const isGlobalIssuesPath = urlParts[0] === 'issues'
  const owner = isGlobalIssuesPath ? undefined : urlParts[0]
  const repo = isGlobalIssuesPath ? undefined : urlParts[1]
  if (owner && repo) {
    await manager.fetchImplicitContext(url, owner, repo, true)
  } else {
    await manager.fetchImplicitContext(url, '', '', true)
  }
}

export interface CopilotChatManagerProviderProps {
  apiURL: string
  state: CopilotChatState
  dispatch: Dispatch<CopilotChatAction>
  ssoOrganizations: CopilotChatOrg[]
  realIp?: string
  hasCEorCBAccess?: boolean
  apiVersion?: string
  enableV1CitationMarkerSanitization?: boolean
  waitForInitialInstructionRules?: boolean
  instructionRulesLoadingFallback?: ReactNode
}

export function CopilotChatManagerProvider({
  apiURL,
  state,
  dispatch,
  ssoOrganizations,
  children,
  realIp,
  hasCEorCBAccess,
  apiVersion,
  enableV1CitationMarkerSanitization = true,
  waitForInitialInstructionRules = false,
  instructionRulesLoadingFallback = null,
}: PropsWithChildren<CopilotChatManagerProviderProps>) {
  const getChatState = useGetChatState()
  const plugins = usePlugins()
  const traceDispatch = useTraceDispatchMaybe()
  const {reloadQuota, plan} = useEntitlement()
  const reloadQuotaRef = useRef(reloadQuota)
  useEffect(() => {
    reloadQuotaRef.current = reloadQuota
  }, [reloadQuota])
  const manager = useMemo(
    () =>
      new CopilotChatManager(
        dispatch,
        apiURL,
        ssoOrganizations,
        getChatState,
        realIp,
        undefined,
        hasCEorCBAccess,
        plugins,
        apiVersion,
        traceDispatch,
        reloadQuotaRef,
        enableV1CitationMarkerSanitization,
      ),
    [
      dispatch,
      apiURL,
      ssoOrganizations,
      getChatState,
      realIp,
      hasCEorCBAccess,
      plugins,
      apiVersion,
      traceDispatch,
      enableV1CitationMarkerSanitization,
    ],
  )

  useEffect(() => {
    return () => manager.clearInstructionRules()
  }, [manager])

  // When the user's Copilot plan changes mid-session (e.g. Free → Pro upgrade), the cached model
  // list is stale: CAPI returns a different list per plan. Force a refresh so the picker stops
  // showing Free-tier models (and the locked Auto entry) to a now-Pro user.
  const previousPlanRef = useRef(plan)
  useEffect(() => {
    const previousPlan = previousPlanRef.current
    previousPlanRef.current = plan
    if (previousPlan != null && plan != null && previousPlan !== plan) {
      void manager.fetchModels({forceRefresh: true})
    }
  }, [manager, plan])

  const selectedThreadIDRef = useRef(state.selectedThreadID)
  // Store the original (unwrapped) history methods so re-runs don't wrap already-wrapped versions
  const originalHistoryMethodsRef = useRef<{
    pushState: History['pushState']
    replaceState: History['replaceState']
  } | null>(null)
  useEffect(() => {
    selectedThreadIDRef.current = state.selectedThreadID
  })

  useEffect(() => {
    const controller = new AbortController()

    // Lazily capture original history methods once (SSR-safe)
    if (!originalHistoryMethodsRef.current) {
      originalHistoryMethodsRef.current = {
        // eslint-disable-next-line @typescript-eslint/unbound-method, no-restricted-properties
        pushState: window.history.pushState,
        // eslint-disable-next-line @typescript-eslint/unbound-method, no-restricted-properties
        replaceState: window.history.replaceState,
      }
    }
    const originals = originalHistoryMethodsRef.current

    const fetchContextPage = async () => {
      // Only fetch implicit context for new threads — skip if the user is in an existing thread
      if (selectedThreadIDRef.current) {
        return
      }

      // While the contextual chat is minimized, navigation must not attach page context.
      if (manager.implicitContextPaused) {
        return
      }

      await fetchImplicitContextForCurrentPage(manager)
    }

    const voidFetch = () => {
      void fetchContextPage()
    }

    const debouncedFetch = debounce(() => voidFetch(), 500)

    function watchHistoryEvents() {
      const {pushState, replaceState} = originals

      // eslint-disable-next-line no-restricted-properties
      window.history.replaceState = function (...args) {
        // eslint-disable-next-line no-restricted-properties
        replaceState.apply(window.history, args)
        window.dispatchEvent(new Event('replaceState'))
      }

      // pushState is called by the browser when the user navigates to a new page. We need to listen
      // for this event to clear and generates suggestions.
      // eslint-disable-next-line no-restricted-properties
      window.history.pushState = function (...args) {
        // eslint-disable-next-line no-restricted-properties
        pushState.apply(window.history, args)
        window.dispatchEvent(new Event('pushState'))
      }

      window.addEventListener('popstate', debouncedFetch, {signal: controller.signal})
      window.addEventListener('replaceState', debouncedFetch, {signal: controller.signal})
      window.addEventListener('pushState', debouncedFetch, {signal: controller.signal})
    }

    if (state.chatIsOpen && !state.skipImplicitReferences) {
      voidFetch()
    }
    watchHistoryEvents()

    return () => {
      controller.abort()
      // Restore original history methods to prevent stacking wrappers on re-runs
      const {pushState, replaceState} = originals
      // eslint-disable-next-line no-restricted-properties
      window.history.pushState = pushState
      // eslint-disable-next-line no-restricted-properties
      window.history.replaceState = replaceState
    }
  }, [manager, state.chatIsOpen, state.skipImplicitReferences, state.selectedThreadID])

  return (
    <InstructionRulesBoundary
      key={waitForInitialInstructionRules ? getManagerKey(manager) : 'ungated'}
      manager={manager}
      state={state}
      waitForInitialInstructionRules={waitForInitialInstructionRules}
      loadingFallback={instructionRulesLoadingFallback}
    >
      {children}
    </InstructionRulesBoundary>
  )
}

function InstructionRulesBoundary({
  manager,
  state,
  waitForInitialInstructionRules,
  loadingFallback,
  children,
}: PropsWithChildren<{
  manager: CopilotChatManager
  state: CopilotChatState
  waitForInitialInstructionRules: boolean
  loadingFallback: ReactNode
}>) {
  const shouldWait = waitForInitialInstructionRules
  const readyRef = useRef(!shouldWait)
  const [ready, setReady] = useState(!shouldWait)
  const [error, setError] = useState<Error>()

  useEffect(() => {
    let cancelled = false

    void (async () => {
      try {
        await manager.updateInstructionRules({
          personalInstructions: state.personalInstructions,
          customInstructions: state.customInstructions,
          references: state.repoCustomInstructionsEnabled ? state.currentReferences : [],
        })
        if (!cancelled) {
          readyRef.current = true
          setReady(true)
        }
      } catch (caughtError) {
        if (cancelled) return
        const publicationError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Failed to publish Copilot custom instructions.', {cause: caughtError})
        if (shouldWait && !readyRef.current) {
          setError(publicationError)
        } else {
          // eslint-disable-next-line no-console
          console.error('[copilot-chat] failed to publish custom instructions', publicationError)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    manager,
    shouldWait,
    state.currentReferences,
    state.customInstructions,
    state.personalInstructions,
    state.repoCustomInstructionsEnabled,
  ])

  if (error) throw error

  return <CopilotChatManagerContext value={manager}>{ready ? children : loadingFallback}</CopilotChatManagerContext>
}

export function useChatManager() {
  const context = use(CopilotChatManagerContext)
  if (!context) {
    throw new Error('useChatManager must be used within a CopilotChatManagerProvider')
  }
  return context
}

export function useHasCopilotChatManager(): boolean {
  return use(CopilotChatManagerContext) !== null
}

export function useContributedInstructionSnapshot() {
  const manager = useChatManager()
  return useSyncExternalStore(
    listener => manager.subscribeToContributedInstructions(listener),
    () => manager.getContributedInstructionSnapshot(),
    () => [],
  )
}

export function useOptionalContributedInstructionSnapshot(): ContributedInstructionSnapshot {
  const manager = use(CopilotChatManagerContext)
  return useSyncExternalStore(
    manager ? listener => manager.subscribeToContributedInstructions(listener) : subscribeToNothing,
    manager ? () => manager.getContributedInstructionSnapshot() : getEmptyContributedInstructions,
    getEmptyContributedInstructions,
  )
}

export function useSafeChatManager(): CopilotChatManager | undefined {
  return use(CopilotChatManagerContext) ?? undefined
}
